import { forwardRef, useImperativeHandle, useRef } from 'react';
import { useTranslation } from 'react-i18next';

export interface SectionEditorHandle {
  insertText: (prefix: string, suffix: string, placeholder: string) => void;
}

interface SectionEditorProps {
  content: string;
  onChange: (content: string) => void;
  isReadOnly?: boolean;
  placeholder?: string;
}

const SectionEditor = forwardRef<SectionEditorHandle, SectionEditorProps>(
  ({ content, onChange, isReadOnly = false, placeholder }, ref) => {
    const { t } = useTranslation();
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const resolvedPlaceholder = placeholder || t('editorContent.placeholder');

    useImperativeHandle(ref, () => ({
      insertText: (prefix: string, suffix: string, placeholderText: string) => {
        const textarea = textareaRef.current;
        if (!textarea) return;

        const { selectionStart, selectionEnd, value } = textarea;
        const selectedText = value.substring(selectionStart, selectionEnd);
        const textToInsert = selectedText || placeholderText;

        const newValue =
          value.substring(0, selectionStart) +
          prefix +
          textToInsert +
          suffix +
          value.substring(selectionEnd);

        onChange(newValue);

        // Restore cursor position after React re-render
        requestAnimationFrame(() => {
          const newCursorPos = selectionStart + prefix.length + textToInsert.length;
          textarea.setSelectionRange(newCursorPos, newCursorPos);
          textarea.focus();
        });
      },
    }));

    return (
      <div className="section-editor">
        <textarea
          ref={textareaRef}
          className="editor-textarea"
          value={content}
          onChange={(e) => onChange(e.target.value)}
          disabled={isReadOnly}
          placeholder={resolvedPlaceholder}
          spellCheck
        />
        <div className="editor-hint">
          {t('editorContent.markdownHint')}
        </div>
      </div>
    );
  }
);

SectionEditor.displayName = 'SectionEditor';

export default SectionEditor;
